'use es6';

import { chain, findKey, memoize } from 'underscore';
import I18n from 'I18n';
import { INVALID_CHARS_REGEX, INVALID_CHARS_DOT_ALLOWED_REGEX } from 'DesignData/constants/FilenameValidation';
import { LAYOUT, TEMPLATE, CSS as FOLDER_ASSET_TYPE_CSS, JS, GLOBAL_GROUP, CUSTOM_WIDGET, RAW_ASSET, FOLDER } from 'DesignData/constants/FolderAssetTypes';
import AssetsWithFilenameExtensions from 'DesignData/constants/FolderAssetTypesWithExtensions';
import { HUBSPOT_FOLDER_PATH, HUBSPOT_FOLDER_ID } from 'DesignData/constants/Folders';
import * as FolderSubcategories from 'DesignData/enums/FolderSubcategories';
import { HOLLOW } from 'DesignData/enums/AssetSources';
import { DESIGN } from 'DesignData/enums/FolderCategories';
import { formatPathAsId } from 'DesignData/lib/text';
import * as RawAssetExtensions from 'DesignData/enums/RawAssetExtensions';
import { isServerlessFunctionFolder } from 'DesignData/lib/serverless/serverless';

const i18nKey = key => `designdata.models.finder.${key}`;

const getFolderSubcategoryKey = memoize(subcategory => {
  return findKey(FolderSubcategories, __subcategory => __subcategory === subcategory);
});
const folderSubcategoryKeys = Object.keys(FolderSubcategories || {});
const folderSubcategoriesRegex = new RegExp(folderSubcategoryKeys.join('|'), 'g');
export const getFolderSubcategoryI18nKey = (subcategory, key = 'text') => {
  const subcategoryKey = getFolderSubcategoryKey(subcategory);
  return i18nKey(`Folder.subcategories.${subcategoryKey}.${key}`);
};
export const getFolderDefaultI18nKey = () => {
  return i18nKey('Folder.defaultValue');
};
export const getFileTypeI18nKey = memoize(fileType => {
  return i18nKey(`File.types.${fileType}`);
});
export const translateFolderPath = memoize((__folderPath = '') => {
  if (!__folderPath) {
    return null;
  }

  let folderPath = __folderPath;

  if (folderPath.charAt(0) === '/') {
    folderPath = folderPath.slice(1);
  }

  const folderSubcategoriesMatches = folderPath.match(folderSubcategoriesRegex);

  if (folderSubcategoriesMatches) {
    folderSubcategoriesMatches.forEach(subcategory => {
      const subcategoryValue = FolderSubcategories[subcategory];

      if (subcategoryValue === FolderSubcategories.DESIGN_ROOT) {
        folderPath = folderPath.replace(subcategory, '');
        return;
      }

      const subcategoryI18nKey = getFolderSubcategoryI18nKey(subcategoryValue);
      const subcategoryTranslation = I18n.text(subcategoryI18nKey);
      folderPath = folderPath.replace(subcategory, subcategoryTranslation);
    });
  }

  folderPath = folderPath.replace(/_/g, ' ');
  return folderPath || null;
});
export function generateCopyName(name, assetType, assets) {
  const copySuffix = I18n.text(i18nKey('copySuffix'));
  const copyRegex = new RegExp(`[\\s\\_]${copySuffix}([\\s\\_]\\d+)?$`, 'i');
  const invalidCharsRegex = AssetsWithFilenameExtensions.includes(assetType) ? INVALID_CHARS_DOT_ALLOWED_REGEX : INVALID_CHARS_REGEX;
  const baseName = name.replace(copyRegex, '').replace(invalidCharsRegex, '').trim();
  const baseNameRegex = new RegExp(baseName, 'i');
  const existingCopyNames = chain(assets).filter(__asset => {
    const assetBaseName = __asset.name.replace(copyRegex, '').trim();

    return baseNameRegex.test(assetBaseName);
  }).pluck('name').value();
  let copyNumber = existingCopyNames.length + 1;

  for (let i = 1; i <= existingCopyNames.length; i++) {
    const existingCopyNameRegex = i === 1 ? new RegExp(`${copySuffix}$`, 'i') : new RegExp(`${copySuffix}[\\s\\_]${i}\$`, 'i');
    const existingCopyName = existingCopyNames.find(__copyName => {
      return existingCopyNameRegex.test(__copyName);
    });

    if (!existingCopyName) {
      copyNumber = i;
      break;
    }
  }

  return copyNumber === 1 ? `${baseName} ${copySuffix}` : `${baseName} ${copySuffix} ${copyNumber}`;
}
export function getFileDataFromResult(fileType, result) {
  let file = {
    id: result.id,
    folderId: result.folderId,
    folderPath: result.folderPath,
    path: result.path,
    type: fileType,
    moduleId: result.moduleId,
    assetSource: result.assetSource,
    projectId: result.projectId
  };

  if (fileType === LAYOUT || fileType === TEMPLATE || fileType === FOLDER_ASSET_TYPE_CSS || fileType === JS || fileType === GLOBAL_GROUP) {
    file = Object.assign({}, file, {
      templateType: result.templateType,
      isReadOnly: result.isReadOnly
    });
  }

  if (fileType === TEMPLATE) {
    let availableForNewContent;

    if (typeof result.isAvailableForNewContent !== 'undefined') {
      availableForNewContent = result.isAvailableForNewContent;
    } else {
      availableForNewContent = result.availableForNewContent || false;
    }

    file.availableForNewContent = availableForNewContent;
  }

  if (fileType === CUSTOM_WIDGET) {
    file.name = result.name;
  } else if (fileType === GLOBAL_GROUP) {
    file.name = result.label.split('/').pop();
  } else {
    file.name = result.label;
  }

  if (fileType === GLOBAL_GROUP || fileType === LAYOUT) {
    file = Object.assign({}, file, {
      generatedTemplatePath: result.generatedTemplatePath
    });
  }

  return file;
}
const RecurseFlowControl = {
  CONTINUE: 'CONTINUE',
  SKIP_CHILDREN: 'SKIP_CHILDREN'
};

function recurseFileMapperTree(node, callback) {
  if (!node) return;
  const cbResult = callback(node);
  if (cbResult === RecurseFlowControl.SKIP_CHILDREN) return;
  if (!node.children || !node.children.length) return;
  node.children.forEach(child => recurseFileMapperTree(child, callback));
}
/** Module folders are "files" in Design Manager */


const isModuleNode = node => node.folder && (node.name || '').endsWith('.module');
/** Theme folders are will have children `theme.json` and `fields.json` */


const isThemeNode = node => {
  let hasThemeJson = false;
  let hasFieldsJson = false;
  return !!(node && node.children && node.children.length && node.children.some(childNode => {
    if (childNode.name === 'theme.json') hasThemeJson = true;
    if (childNode.name === 'fields.json') hasFieldsJson = true;
    return hasThemeJson && hasFieldsJson;
  }));
};

function createFolderDataFromFileMapperTreeNode(node) {
  let subcategory;

  if (node.path === HUBSPOT_FOLDER_PATH) {
    subcategory = FolderSubcategories.HUBSPOT_DEFAULT;
  } else if (isThemeNode(node)) {
    subcategory = FolderSubcategories.THEME;
  }

  return {
    subcategory,
    isDefault: true,
    readOnly: true,
    id: formatPathAsId(node.path),
    category: DESIGN,
    type: FOLDER,
    fullPath: node.path,
    label: node.name,
    name: node.name,
    parentFolderId: formatPathAsId(node.parentPath)
  };
}

const ExtensionToFolderAssetTypes = Object.assign({}, Object.keys(RawAssetExtensions).reduce((hash, key) => {
  hash[key] = RAW_ASSET;
  return hash;
}, {}), {
  module: CUSTOM_WIDGET,
  css: FOLDER_ASSET_TYPE_CSS,
  js: JS,
  html: TEMPLATE
});

const getExt = path => {
  if (!path) return null;
  const parts = path.split('.');
  if (parts.length) return parts.pop();
  return null;
};

const trimExtension = path => {
  if (!path) return path;
  const parts = path.split('.');
  if (!parts.length) return path;
  parts.pop();
  return parts.join('/');
};

function createFileDataFromFileMapperTreeNode(node) {
  const extension = getExt(node.path);
  const type = ExtensionToFolderAssetTypes[extension];
  const {
    name
  } = node;
  let {
    path
  } = node;

  if (type === CUSTOM_WIDGET) {
    // Module API's do not work with `.module` extension
    path = trimExtension(path);
  }

  return {
    type,
    name,
    path,
    isDefault: true,
    isReadOnly: true,
    folderId: formatPathAsId(node.parentPath),
    folderPath: node.parentPath,
    id: formatPathAsId(path),
    assetSource: HOLLOW,
    updated: node.updatedAt
  };
}
/**
 * Creates file data for Hubspot defaults from filemapper API tree
 * @param {Object} tree
 * @returns {Array<File>}
 */


export function getFileAndFolderDataFromFileMapperTree(tree) {
  let folder;
  const subfolders = [];
  const files = [];
  recurseFileMapperTree(tree, node => {
    const isModule = isModuleNode(node);

    if (node.path === HUBSPOT_FOLDER_PATH) {
      folder = createFolderDataFromFileMapperTreeNode(node);
    } else if (isModule || !node.folder) {
      files.push(createFileDataFromFileMapperTreeNode(node));
    } else {
      subfolders.push(createFolderDataFromFileMapperTreeNode(node));
    }

    if (isModule) {
      return RecurseFlowControl.SKIP_CHILDREN;
    }

    return RecurseFlowControl.CONTINUE;
  });
  return {
    folder,
    subfolders,
    files
  };
}

const canMoveToThemeFolder = (targetFolder, props) => {
  return targetFolder && targetFolder.subcategory === FolderSubcategories.THEME && props.activeThemeFolderId && props.activeThemeFolderId === targetFolder.id;
};

export const isHollowFolderId = folderId => {
  return typeof folderId === 'string' && (folderId.startsWith(HUBSPOT_FOLDER_PATH) || folderId.startsWith(HUBSPOT_FOLDER_ID));
};
export const canMoveFolder = (targetFolder, folder, props) => {
  if (props.isLocked || props.isAncestorLocked) {
    return false;
  }

  if (targetFolder.id === folder.parentFolderId) {
    return true;
  }

  if (targetFolder.readOnly || folder.readOnly) {
    return false;
  }

  if (targetFolder.subcategory === FolderSubcategories.PROJECT) {
    return false;
  } // Block moving assets to subcategory folders until themes exist


  if (targetFolder.subcategory && targetFolder.subcategory !== FolderSubcategories.DESIGN_ROOT) {
    // TEMPORARY HACK
    if (canMoveToThemeFolder(targetFolder, props)) {
      return true;
    }

    return false;
  } // Folders can't be moved to themselves


  if (targetFolder.id === folder.id) {
    return false;
  } // Block moving subcategory folders until themes exist


  if (folder.subcategory) {
    return false;
  }

  return true;
};
export const canMoveFile = (targetFolder, file, props) => {
  if (props.isLocked || props.isAncestorLocked) {
    return false;
  }

  if (targetFolder.id === file.parentFolderId) {
    return true;
  }

  if (isHollowFolderId(targetFolder.id)) {
    return false;
  }

  if (isServerlessFunctionFolder(targetFolder) && file.type !== 'RAW_ASSET') {
    return false;
  }

  if (targetFolder.subcategory === FolderSubcategories.PROJECT) {
    return false;
  }

  if (targetFolder.subcategory && targetFolder.subcategory !== FolderSubcategories.DESIGN_ROOT) {
    if (canMoveToThemeFolder(targetFolder, props) || file.type !== 'LAYOUT') {
      return true;
    }

    return false;
  }

  return true;
};